import axios from 'axios';
import { toast } from 'sonner';
import { setPreviewSession } from './previewSession';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Maps a target user role to the URL their portal lives at
const PORTAL_LANDING = {
  customer: '/customer/dashboard',
  vip: '/vip/dashboard',
  employee: '/employee/timeclock',
  technician: '/employee/timeclock',
  manager: '/admin/dashboard',
  service_manager: '/admin/dashboard',
  construction_manager: '/admin/dashboard',
  office_staff: '/admin/dashboard',
  property_manager: '/pm/dashboard',
  asset_manager: '/pm/dashboard',
  maintenance_supervisor: '/pm/dashboard',
};

// Friendly verbiage for the confirm dialog + toast
const ROLE_VERB = {
  customer: 'Customer',
  vip: 'VIP',
  employee: 'Employee',
  technician: 'Technician',
  manager: 'Manager',
  service_manager: 'Service Manager',
  construction_manager: 'Construction Manager',
  office_staff: 'Office Staff',
  property_manager: 'Property Manager',
  asset_manager: 'Asset Manager',
  maintenance_supervisor: 'Maintenance Supervisor',
};

/**
 * Start a read-only portal preview for the given user record.
 * `userLike` shape: { id, name, email, role }
 * Performs a full page reload (window.location.href) to land on the
 * target portal so AuthContext + every route guard re-reads the new
 * X-Preview-As-User-Id header cleanly.
 */
export async function startPortalPreview(userLike) {
  if (!userLike?.id) {
    toast.error('Missing user id');
    return;
  }
  const role = userLike.role || 'user';
  const verb = ROLE_VERB[role] || 'user';
  const forCompany = userLike.context ? ` for ${userLike.context}` : '';
  const okMsg = `View ${userLike.name}'s ${verb} portal${forCompany} in READ-ONLY mode?\n\nYou will not be able to submit, edit, upload, or delete anything until you exit preview.`;
  if (!window.confirm(okMsg)) return;
  try {
    const { data } = await axios.post(
      `${API}/admin/preview/start`,
      { user_id: userLike.id },
      { withCredentials: true },
    );
    setPreviewSession({
      user_id: userLike.id,
      name: userLike.name,
      email: userLike.email,
      role,
      context: userLike.context || null,
      started_at: data?.preview?.started_at,
    });
    toast.success(`Previewing as ${userLike.name} (read-only)`);
    const land = PORTAL_LANDING[role] || '/admin/dashboard';
    // Full reload so AuthContext + axios interceptors re-init under the preview cookie/header.
    window.location.href = land;
  } catch (err) {
    toast.error(err.response?.data?.detail || 'Failed to start preview');
  }
}
